import { useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { ReviewWizard, WizardData } from "@/components/wizard";
import { useContractReview } from "@/hooks/useContractReview";
import { ReviewProgressLayout } from "@/components/review/ReviewProgressLayout";
import { ErrorAlert } from "@/components/common/ErrorAlert";
import { useApp, AppProvider } from "@/contexts/AppContext";
import { darkPageCanvasBackgroundClassName } from "@/lib/page-canvas";
import { cn } from "@/lib/utils";

function NewReviewPageContent() {
  const navigate = useNavigate();
  const { startReview, isProcessing } = useContractReview();
  const { state, reset, setError } = useApp();

  const handleSubmit = useCallback(
    async (data: WizardData) => {
      localStorage.setItem(
        "reviewFormData",
        JSON.stringify({
          backgroundInfo: data.backgroundInfo,
          agentInstructions: data.agentInstructions,
          contractType: data.contractType,
          jurisdiction: data.jurisdiction,
          userRole: data.userRole,
          roleInstructions: "",
          skipResearch: data.skipResearch,
        })
      );

      const completion = await startReview({
        backgroundInfo: data.backgroundInfo,
        agentInstructions: data.agentInstructions,
        contractType: data.contractType,
        jurisdiction: data.jurisdiction,
        userRole: data.userRole,
        roleInstructions: "",
        skipResearch: data.skipResearch,
        supportingDocs: data.supportingDocs,
      }, data.selectedFile, data.selectedDriveFile);

      if (completion?.reviewId) {
        reset();
        navigate(`/contract-review/${completion.reviewId}`, { replace: true });
        return;
      }

      setError("Review completed, but no review ID was returned by the server.");
    },
    [navigate, reset, setError, startReview]
  );

  if (isProcessing || state.progress.progress > 0 || state.results) {
    return (
      <ReviewProgressLayout
        title={state.results ? "Finalizing Review..." : "Analyzing Contract..."}
        progress={state.results ? undefined : state.progress}
      >
        {state.results ? (
          <div className="flex items-center justify-center gap-3 text-slate-400">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-violet-500" />
            <span>Preparing your review...</span>
          </div>
        ) : null}
      </ReviewProgressLayout>
    );
  }

  return (
    <div
      className={cn(
        "relative flex w-full min-h-full shrink-0 grow-0 flex-col",
        darkPageCanvasBackgroundClassName,
      )}
    >
      <header className="relative border-b border-[var(--lm-border-subtle)] bg-[var(--bar-bg)] px-6 py-4 backdrop-blur-sm dark:border-slate-800/50 dark:bg-slate-900/80">
        <div className="flex items-center gap-4">
          <IconButton asChild title="Back to contract reviews">
            <Link to="/contract-review">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </IconButton>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-violet-500/10 p-2">
              <FileText className="h-5 w-5 text-violet-400" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
                New Contract Review
              </h1>
              <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">
                Upload and configure your contract analysis
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="relative px-6 py-8">
        <div className="mx-auto max-w-4xl">
          {state.error && (
            <div className="mb-6">
              <ErrorAlert message={state.error} />
            </div>
          )}
          <ReviewWizard onSubmit={handleSubmit} isSubmitting={isProcessing} />
        </div>
      </main>
    </div>
  );
}

export function NewReviewPage() {
  return (
    <AppProvider>
      <NewReviewPageContent />
    </AppProvider>
  );
}
